import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enUS from "@/locale/en_US.json";
import arSA from "@/locale/ar_SA.json";
import {
  getCheckoutLocaleCookie,
  setCheckoutLocaleCookie,
} from "@/services/cookie";

const supportedLocales = ["en_US", "ar_SA"] as const;
type SupportedLocale = (typeof supportedLocales)[number];

export function parseLocale(
  locale: string | undefined,
): SupportedLocale | null {
  if (!locale) return null;

  const lowercased = locale.toLowerCase().trim();

  if (lowercased.startsWith("en")) {
    return "en_US";
  }

  if (lowercased.startsWith("ar")) {
    return "ar_SA";
  }

  return null;
}

export function getLocaleFromUrl(): SupportedLocale | null {
  try {
    const localeParam = new URLSearchParams(window.location.search).get(
      "locale",
    );
    return parseLocale(localeParam ?? undefined);
  } catch {
    return null;
  }
}

function resolveLocaleFromQueryParams(): SupportedLocale | null {
  const parsedLocale = getLocaleFromUrl();
  if (!parsedLocale) {
    return null;
  }

  setCheckoutLocaleCookie(parsedLocale);
  return parsedLocale;
}

function resolveLocale(): SupportedLocale {
  const queryParamLocale = resolveLocaleFromQueryParams();
  if (queryParamLocale) {
    return queryParamLocale;
  }

  return getCurrentLocaleFromCookie();
}

const initialLocale = resolveLocale();

i18n.use(initReactI18next).init({
  resources: {
    en_US: { translation: enUS },
    ar_SA: { translation: arSA },
  },
  lng: initialLocale,
  fallbackLng: "en_US",
  supportedLngs: supportedLocales as unknown as string[],
  keySeparator: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

function applyDocumentDirection(lang: string) {
  const html = document.documentElement;
  html.setAttribute("lang", lang);
  const isArabic = lang.startsWith("ar");
  html.setAttribute("dir", isArabic ? "rtl" : "ltr");
}

applyDocumentDirection(initialLocale);

i18n.on("languageChanged", (lng) => {
  applyDocumentDirection(lng);
});

export function getCurrentLocaleFromCookie(): SupportedLocale {
  const cookieLocale = getCheckoutLocaleCookie();
  if (!cookieLocale) return "en_US";

  const trimmed = cookieLocale.trim();
  if (trimmed === "en_US" || trimmed === "ar_SA") {
    return trimmed as SupportedLocale;
  }

  return parseLocale(cookieLocale) ?? "en_US";
}

export function syncLanguageWithCookie() {
  const cookieLocale = getCurrentLocaleFromCookie();
  if (i18n.language !== cookieLocale) {
    i18n.changeLanguage(cookieLocale);
  }
}

/**
 * Resolve locale when checkout hands off to PPUI.
 * Priority: URL ?locale= (checkout OTP) → cookie → session API → default.
 */
export function applyLocaleHandoff(sessionLocale?: string | null): void {
  const urlLocale = getLocaleFromUrl();
  if (urlLocale) {
    setCheckoutLocaleCookie(urlLocale);
    if (i18n.language !== urlLocale) {
      void i18n.changeLanguage(urlLocale);
    }
    return;
  }

  const cookieLocale = getCheckoutLocaleCookie();
  if (cookieLocale) {
    syncLanguageWithCookie();
    return;
  }

  if (sessionLocale) {
    const parsedSessionLocale = parseLocale(sessionLocale) ?? "en_US";
    setCheckoutLocaleCookie(parsedSessionLocale);
    void i18n.changeLanguage(parsedSessionLocale);
    return;
  }

  syncLanguageWithCookie();
}

export default i18n;
